import { useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { Box } from '@chakra-ui/react'
import { useInViewRef } from 'rooks'

import { DeviceFeed } from '@/features/device-feed'
import {
  DRAWER_VIDEO_PLAYER_HEIGHT,
  DRAWER_VIDEO_PLAYER_WIDTH,
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
  useIncidentVideo,
} from '@/features/video-player'
import { IncidentType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, PlayerEvents, useMixpanel } from '@/utils/analytics'

import { MixpanelDataIProps } from '../types/types'
import { ShowVideoButton } from './ShowVideoButton'

export interface IncidentVideoIProps {
  incidentId: string
  deviceId: string
  incidentType: IncidentType
  isLoading: boolean
  scrollToTop: () => void
  mixpanelData: MixpanelDataIProps
  hasVideoClip: boolean
}

export const IncidentVideo = ({
  incidentId,
  deviceId,
  incidentType,
  isLoading,
  scrollToTop,
  mixpanelData,
  hasVideoClip,
}: IncidentVideoIProps) => {
  const [isPipActive, setPIPActive] = useState(false)
  const [playerUrl, setPlayerUrl] = useState(null)
  const [isLiveStreamEnabled, setLiveStreamEnabled] = useState(false)
  const [videoRef, isInView] = useInViewRef()

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const {
    trackEvent,
    markStreamRequested,
    createPlayerActionHandler,
    createIncidentVideoEventHandler,
    createCameraEventHandler,
  } = useMixpanel(AnalyticsScreen.ActiveIncidentDrawer)

  const handlePlayerAction = createPlayerActionHandler(mixpanelData)
  const handleIncidentVideoEvent = createIncidentVideoEventHandler(mixpanelData)
  const handleCameraEvent = createCameraEventHandler(mixpanelData)

  const {
    availableCamerasIds,
    isDeviceDataLoading,
    errorMessage: feedError,
    nextCamera,
    previousCamera,
    currentCameraId,
    stream: { url: cameraFeedUrl, isLoading: isFeedDataLoading },
  } = useFindDeviceFeed(deviceId, handleCameraEvent, isLiveStreamEnabled)

  const {
    url: videoUrl,
    isLoading: isVideoUrlLoading,
    errorMessage: incidentError,
    isProcessing,
  } = useIncidentVideo({
    hasVideoClip: hasVideoClip,
    incidentId: incidentId,
    incidentType: incidentType,
    onEvent: handleIncidentVideoEvent,
    skip: !hasVideoClip || isDeviceDataLoading,
  })

  const isVideoLoading =
    isLoading ||
    (isLiveStreamEnabled && isFeedDataLoading) ||
    (!isLiveStreamEnabled && isVideoUrlLoading)

  const disablePIP = () => setPIPActive(false)
  const enablePIP = () => setPIPActive(true)

  useEffect(() => {
    setPlayerUrl(isLiveStreamEnabled ? cameraFeedUrl : videoUrl)
  }, [videoUrl, cameraFeedUrl, isLiveStreamEnabled])

  useEffect(() => {
    if (isLiveStreamEnabled) {
      markStreamRequested()
      trackEvent(PlayerEvents.toggleLiveFeed, mixpanelData)
    } else {
      trackEvent(PlayerEvents.toggleIncidentClip, mixpanelData)
    }
  }, [isLiveStreamEnabled])

  const handleStartPlaying = () => {
    if (isLiveStreamEnabled) {
      handleCameraEvent(VideoFeedEvent.Playing)
    } else {
      handleIncidentVideoEvent(VideoFeedEvent.Playing)
    }
  }

  return (
    hasVideoClip && (
      <>
        <Box
          bg='white'
          data-testid='incidentsPage_drawer_incidentVideo'
          position='sticky'
          pt='20px'
          ref={videoRef}
          top='-352px'
          zIndex='sticky'
        >
          <Box px='24px' w='100%'>
            <DeviceFeed
              isLoading={isVideoLoading}
              onClickNext={nextCamera}
              onClickPrevious={previousCamera}
              showCameraSwitcher={availableCamerasIds.length > 1}
            >
              {isMSEStreamingEnabled &&
              isLiveStreamEnabled &&
              currentCameraId ? (
                <MSEVideoPlayer
                  autoplay={true}
                  cameraId={currentCameraId}
                  errorLabel={feedError}
                  hideSkipButtons={true}
                  liveEnabled={isLiveStreamEnabled}
                  liveOnly={false}
                  onEvent={handleCameraEvent}
                  onPlayerControlAction={handlePlayerAction}
                  onStartPlaying={handleStartPlaying}
                  onToggleLive={setLiveStreamEnabled}
                  placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
                  placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
                  showToggleLiveButton={true}
                />
              ) : (
                <VideoPlayer
                  autoplay={true}
                  disablePIP={disablePIP}
                  errorLabel={isLiveStreamEnabled ? feedError : incidentError}
                  isProcessing={isProcessing}
                  liveEnabled={isLiveStreamEnabled}
                  onPlayerControlAction={handlePlayerAction}
                  onStartPlaying={handleStartPlaying}
                  onToggleLive={setLiveStreamEnabled}
                  pipActive={isPipActive}
                  placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
                  placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
                  url={playerUrl}
                />
              )}
            </DeviceFeed>
          </Box>
        </Box>
        <Box
          bg='white'
          boxShadow='lg'
          position='sticky'
          top='0'
          zIndex='sticky'
        >
          <ShowVideoButton
            disablePIP={disablePIP}
            enablePIP={enablePIP}
            onClick={scrollToTop}
            pipActive={isPipActive}
            show={playerUrl && !isInView}
            url={playerUrl}
          />
        </Box>
      </>
    )
  )
}
